import React, { useCallback, useState } from 'react';

import { normalizedTranslate } from '@rbilabs/components-library';
import { Icon } from '@rbilabs/components-library/build/components/icon';
import { useIdCounter } from '@rbilabs/components-library/build/hooks/use-id-counter';
import delve from 'dlv';
import { useIntl } from 'react-intl';
import { Link } from 'react-router';
import styled from 'styled-components';

import { ISection } from '@rbi-ctg/menu';
import { ActionButton, ActionButtonVariants } from 'components/action-button';
import { FormatMenuPrice } from 'components/menu-utils/format-menu-price';
import { Picture } from 'components/picture';
import { Calories } from 'components/price-and-calories';
import { SanityBlockRenderer } from 'components/sanity-block-renderer';
import { useFormatCalories } from 'hooks/use-format-calories';
import { useLegacyMenuOptionsContext } from 'state/menu-options';
import { getFormattedCalorieRange, getMenuOptionUrl, handleMenuOptionPrefetch } from 'utils/menu';

import { MIN_OPTIONS, OPTION_HEIGHT } from './constants';
import { theme } from './theme';

interface TileOptionsProps {
  isExpanded: boolean;
  hasMoreThanMinOptions: boolean;
  children?: React.ReactNode;
}

const Tile = styled.div`
  display: flex;
  flex-direction: column;
  text-decoration: none;
  transition: all 0.2s;
  margin: 0;

  ${Styles.desktop} {
    margin: 2rem 6rem;
    margin-block-end: 4rem;
  }

  ${Styles.mobile} {
    padding-block-start: 0;
    padding-block-end: 1rem;
    padding-inline: 1rem;
    margin-block-end: 2rem;
  }
`;

const TileContent = styled.div`
  background: ${Styles.color.white};
  border-radius: ${Styles.borderRadius};
  box-shadow: ${Styles.boxShadow};
  display: flex;
  flex-direction: column;
  overflow: hidden;

  ${Styles.desktop} {
    flex-direction: row;
  }
`;

const TileTitle = styled.div`
  background: ${Styles.color.grey.five};
  padding: 12px;
  text-align: center;
  width: 100%;

  h1 {
    color: ${Styles.color.black};
    font-family: ${Styles.fontFamily.base};
    line-height: 1;
    margin-block-start: 0;
    margin-block-end: 1rem;
    margin-inline: 0;
    text-transform: ${Styles.textTransform.headlines};
  }
`;

const SectionDescriptionRenderer = styled(SanityBlockRenderer)`
  font-size: 1.1rem;
  line-height: 1.3;
  color: ${theme.sectionDescriptionRendererColor};
  margin-block-start: -0.5rem;
  margin-block-end: 1rem;
  margin-inline: 0;
`;

const TileImageWrapper = styled.div`
  height: 300px;
  overflow: hidden;
  position: relative;
  width: 100%;

  ${Styles.desktop} {
    width: 50%;
    height: auto;
    max-height: ${MIN_OPTIONS * OPTION_HEIGHT}px;
  }
`;
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const TileOptions = styled(({ isExpanded, hasMoreThanMinOptions, ...rest }: TileOptionsProps) => (
  <div {...rest} />
))`
  border-block-start: 0.5px solid ${Styles.color.grey.four};
  display: flex;
  flex-direction: column;
  min-height: auto;
  padding-block-end: 1.5rem;
  width: 100%;

  ${Styles.desktop} {
    border-inline-start: 0.5px solid ${Styles.color.grey.four};
    border-block-start: none;
    width: 50%;
  }

  a:nth-of-type(n + ${MIN_OPTIONS}):last-child {
    border-block-end: none;
  }

  a:nth-of-type(${MIN_OPTIONS}) {
    border-block-end: ${({ hasMoreThanMinOptions, isExpanded }) =>
      hasMoreThanMinOptions && !isExpanded ? 'none' : `0.5px solid ${Styles.color.grey.four}`};
  }
`;

const StackedTileOption = styled(Link)`
  cursor: pointer;
  overflow: ellipsis;
  text-decoration: none;
  height: ${OPTION_HEIGHT}px;
  position: relative;
  padding-block-start: 1.5rem;
  margin: 0 1rem;
  border-block-end: 0.5px solid ${Styles.color.grey.four};

  ${Styles.desktop} {
    margin: 0 1.5rem;
  }
`;

const OptionDescription = styled.div`
  height: 100%;
  margin-inline-end: 1rem;
  width: 100%;

  h3 {
    color: ${Styles.color.black};
    font-family: ${Styles.fontFamily.base};
    line-height: 0.9;
    margin: 0;
    text-transform: ${Styles.textTransform.headlines};
  }

  p {
    font-family: ${Styles.fontFamily.body};
  }
`;

const NameAndCalories = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  h3 {
  }

  p {
    color: ${Styles.color.grey.two};
    margin: 0;
    font-size: 0.875rem;
    text-align: end;
  }
`;

const Price = styled.p`
  flex-shrink: 0;
  flex-grow: 0;
  display: flex;
  justify-content: flex-start;
  margin: 0;
  padding-block-end: 1rem;

  span {
    color: ${Styles.color.grey.two};
    font-weight: ${Styles.fontWeight.heavy};
  }
`;

export const ExpanderWrapper = styled.div`
  position: relative;
`;

export const ToggleButtonWrapper = styled.div<{ isExpanded: boolean }>`
  position: absolute;
  inset-block-end: 0;
  inset-inline-start: 50%;
  transform: ${normalizedTranslate('-50%', '50%')};

  ${Styles.desktop} {
    inset-inline-start: 75%;
  }

  svg {
    transform: ${({ isExpanded }) => (isExpanded ? 'rotate(180deg)' : 'none')};
    transition: transform 0.4s cubic-bezier(0, 1.15, 0.3, 1.25);
  }
`;

const PriceDescriptionWrapper = styled.div`
  display: flex;
  margin-block-start: 0.5rem;
`;

const OptionDescriptionRenderer = styled(SanityBlockRenderer)`
  display: none;
  flex-grow: 1;
  color: ${theme.optionDescriptionRendererColor};
  font-size: 0.8rem;
  margin-block-start: -0.15rem;
  margin-inline-end: 0.5rem;
`;

interface IMenuTileStackProps {
  description: ISection['description'];
  image: ISection['image'];
  name: string;
  options: ISection['options'];
}

export const MenuTileStack: React.FC<IMenuTileStackProps> = React.memo(props => {
  const id = useIdCounter('options-tile');
  const { formatMessage } = useIntl();
  const formatCalories = useFormatCalories();
  const { getMenuObject } = useLegacyMenuOptionsContext();

  const [isExpandedNoFeatureFlag, toggleExpandState] = useState(false);
  const toggleStackTiles = () => toggleExpandState(!isExpandedNoFeatureFlag);
  const isExpanded = isExpandedNoFeatureFlag;

  // Only show 5 options if we are not expanded
  const hasMoreThanMinOptions = props.options && props.options.length > MIN_OPTIONS;
  const options = props.options
    ? props.options.slice(0, isExpanded ? props.options.length : MIN_OPTIONS)
    : [];

  const StackedOptions = useCallback(() => {
    return (
      <>
        {options.map((option, i) => {
          return (
            <StackedTileOption
              onMouseOver={handleMenuOptionPrefetch(getMenuObject, option)}
              onTouchStart={handleMenuOptionPrefetch(getMenuObject, option)}
              key={option._id}
              to={getMenuOptionUrl(option)}
              data-testid={`stack-tile-${i}`}
            >
              <OptionDescription>
                <NameAndCalories>
                  <h3
                    data-testid={`stack-tile-name-${option.name?.locale
                      .toLowerCase()
                      .replace(/\s/g, '')}`}
                  >
                    {option?.name?.locale}
                  </h3>
                  <Calories>
                    <p data-testid="stack-tile-calories">
                      {getFormattedCalorieRange(option, formatCalories)}
                    </p>
                  </Calories>
                </NameAndCalories>
                <PriceDescriptionWrapper>
                  <OptionDescriptionRenderer content={delve(option, 'description.localeRaw')} />
                  <Price>
                    <span data-testid="stack-tile-price">
                      <FormatMenuPrice menuItem={option} />
                    </span>
                  </Price>
                </PriceDescriptionWrapper>
              </OptionDescription>
            </StackedTileOption>
          );
        })}
      </>
    );
  }, [formatCalories, options]);

  if (!options.length) {
    return null;
  }

  return (
    <Tile data-testid={`menu-stack-tile-${props.name}`}>
      <TileTitle>
        <h1>{props.name}</h1>
        <SectionDescriptionRenderer content={delve(props, 'description.localeRaw')} />
      </TileTitle>

      <TileContent>
        <TileImageWrapper>
          <Picture image={props.image} alt="" />
        </TileImageWrapper>

        <TileOptions
          hasMoreThanMinOptions={hasMoreThanMinOptions}
          isExpanded={isExpanded}
          data-testid="menu-stack-tile-list"
        >
          <StackedOptions />
        </TileOptions>
      </TileContent>

      {hasMoreThanMinOptions && (
        <ExpanderWrapper>
          <ToggleButtonWrapper isExpanded={isExpanded}>
            <ActionButton
              variant={ActionButtonVariants.OUTLINE}
              onClick={toggleStackTiles}
              aria-expanded={isExpanded}
              aria-controls={id}
              data-testid="expander"
              endIcon={<Icon icon="select" color="primary" />}
            >
              {isExpanded
                ? formatMessage({ id: 'lessOptions' })
                : formatMessage({ id: 'moreOptions' })}
            </ActionButton>
          </ToggleButtonWrapper>
        </ExpanderWrapper>
      )}
    </Tile>
  );
});
